<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { RecordStageModal } from '@/entities/GeneratedRecord';
  import { FillRenderer, FormBuilderFooter, useSheetFit } from '@/features/FormBuilder';
  import { readMatrixReturnFolder } from '@/shared/lib';
  import { SimpleLoading } from '@/shared/ui';
  import { readRecordIndexerQuery, withRecordIndexerQuery } from '../model/record-indexing';
  import { useGenerateRecordPage } from '../model/useGenerateRecordPage';
  import { useSheetPages } from '../model/useSheetPages';
  import { useSheetZoom } from '../model/useSheetZoom';
  import CorrectionModeStatus from './CorrectionModeStatus.vue';
  import GenerateRecordActionBar from './GenerateRecordActionBar.vue';
  import RecordAttachmentsSection from './RecordAttachmentsSection.vue';
  import RecordIndexerModal from './RecordIndexerModal.vue';

  const route = useRoute();
  const router = useRouter();
  // The router already decodes the params; decoding again would break a literal "%".
  const matrixId = computed(() => String(route.params.matrixId ?? ''));
  const prefix = computed(() => String(route.params.prefix ?? ''));
  const recordId = computed(() => String(route.params.recordId ?? ''));
  const identifier = computed(() =>
    typeof route.query.identifier === 'string' ? route.query.identifier : ''
  );
  const indexer = computed(() => readRecordIndexerQuery(route.query));

  function pageOptions() {
    if (route.name === 'PreencherRegistro') return { recordId };
    if (route.name === 'EditarRegistro') return { recordId, editing: true };
    if (route.name === 'VisualizarRegistro') return { recordId, viewing: true };
    return { identifier, indexer };
  }

  const {
    isFilling,
    isViewing,
    loading,
    title,
    matrixPrefix,
    recordLine,
    stages,
    showExportPdf,
    compiled,
    totalFields,
    selectedFieldId,
    extraIssues,
    saveAttempted,
    saving,
    hasChanges,
    stageModalOpen,
    indexerModalOpen,
    indexerError,
    indexerChecking,
    clearIndexerError,
    acceptIndexer,
    checkIndexer,
    cancelIndexer,
    correction,
    attachments,
    setValue,
    setCellValue,
    addExtraRow,
    removeExtraRow,
    save,
    closeStageModal,
    confirmStage,
    cancel,
  } = useGenerateRecordPage(matrixId, prefix, {
    ...pageOptions(),
    returnFolderCode: readMatrixReturnFolder(route.query),
  });

  const {
    rows: attachmentRows,
    canAttach,
    busy: attachmentsBusy,
    attach: attachFiles,
    open: openAttachment,
    remove: removeAttachment,
  } = attachments;

  const {
    active: correctionActive,
    pulsing: correctionPulsing,
    marks: correctionMarks,
    changeCount: correctionChangeCount,
    leave: leaveCorrection,
  } = correction;

  const scrollRef = ref<HTMLElement | null>(null);
  const sheetRef = ref<HTMLElement | null>(null);
  const { zoom, setZoomPercent, onWheel } = useSheetZoom();
  const showAnchorHighlights = ref(false);

  const orientation = computed(() => compiled.value?.orientation ?? 'portrait');
  const { sheetWidth: displayWidth, fitScale } = useSheetFit(scrollRef, orientation);
  const scale = computed(() => fitScale.value * zoom.value);
  const { pageCount, currentPage, scaledHeight, scaledWidth, onScroll } = useSheetPages({
    sheetRef,
    scrollRef,
    displayWidth,
    zoom: scale,
    orientation,
  });

  function onFocusField(fieldId: string): void {
    selectedFieldId.value = fieldId;
  }

  function onZoom(value: number): void {
    setZoomPercent(Math.round(value * 100));
  }

  function toggleAnchorHighlights(): void {
    showAnchorHighlights.value = !showAnchorHighlights.value;
  }

  // The query keeps the indexer across a reload; replace, so Voltar does not reopen the modal.
  async function createWithIndexer(typed: string): Promise<void> {
    if (!(await checkIndexer(typed))) return;
    await router.replace({ query: withRecordIndexerQuery(route.query, typed) });
    acceptIndexer();
  }
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full flex-col"
    data-testid="generate-record-page"
  >
    <div
      v-if="loading"
      class="flex flex-1 items-center justify-center px-6"
      data-testid="generate-record-loading"
    >
      <SimpleLoading />
    </div>

    <template v-else-if="compiled">
      <div class="sticky top-0 z-10 bg-white-02 px-6 pt-4 pb-4">
        <h1
          class="truncate text-left text-xl/7.5 font-semibold text-gray-07"
          :title="title"
          data-testid="generate-record-title"
        >
          {{ title }}
        </h1>
        <p
          v-if="recordLine"
          class="mt-1 truncate text-[13px]/[18px] text-gray-03"
          :title="recordLine"
          data-testid="generate-record-line"
        >
          {{ recordLine }}
        </p>
        <GenerateRecordActionBar
          class="mt-4"
          :filling="isFilling"
          :viewing="isViewing"
          :can-save="hasChanges && !attachmentsBusy"
          :show-export-pdf="showExportPdf"
          @cancel="cancel"
          @save="save"
        />
        <CorrectionModeStatus
          v-if="correctionActive"
          class="mt-3"
          :change-count="correctionChangeCount"
          @exit="leaveCorrection"
        />
      </div>

      <div
        ref="scrollRef"
        class="min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable] px-6 py-6"
        data-testid="generate-record-scroll"
        @scroll="onScroll"
        @wheel="onWheel"
      >
        <div
          class="mx-auto"
          :style="{ height: `${scaledHeight}px`, width: `${scaledWidth}px` }"
        >
          <div
            class="relative rounded-md"
            :class="{ 'correction-ring': correctionActive, 'correction-pulse': correctionPulsing }"
            :style="{ width: `${displayWidth}px`, transform: `scale(${scale})`, transformOrigin: 'top left' }"
            data-testid="fill-paper-frame"
          >
            <section
              ref="sheetRef"
              class="light-surface rounded-md bg-white-01 p-8 text-gray-07 shadow-md"
              :style="{ width: `${displayWidth}px` }"
              data-testid="fill-paper"
            >
              <FillRenderer
                :form="compiled"
                :read-only="isViewing"
                :highlighted-field-id="selectedFieldId"
                :extra-issues="extraIssues"
                :show-anchor-highlights="showAnchorHighlights"
                :show-compiled-issues="saveAttempted"
                :change-marks="correctionMarks"
                @update:value="setValue"
                @update:cell="setCellValue"
                @add-row="addExtraRow"
                @remove-row="removeExtraRow"
                @focus:field="onFocusField"
              />
            </section>
          </div>
        </div>

        <RecordAttachmentsSection
          :rows="attachmentRows"
          :can-attach="canAttach"
          @attach="attachFiles"
          @open="openAttachment"
          @delete="removeAttachment"
        />
      </div>

      <FormBuilderFooter
        class="sticky bottom-0 z-10"
        mode="view"
        :orientation="orientation"
        :zoom="zoom"
        :page-count="pageCount"
        :current-page="currentPage"
        :total-fields="totalFields"
        total-fields-scope="sheet"
        enable-anchor-tool
        :show-anchor-highlights="showAnchorHighlights"
        @update:zoom="onZoom"
        @toggle-anchor-visibility="toggleAnchorHighlights"
      />

      <RecordStageModal
        v-if="stageModalOpen"
        :stages="stages"
        :submitting="saving"
        @close="closeStageModal"
        @confirm="confirmStage"
      />

      <RecordIndexerModal
        v-if="indexerModalOpen"
        :prefix="matrixPrefix"
        :initial-value="indexer"
        :server-error="indexerError"
        :submitting="indexerChecking"
        @cancel="cancelIndexer"
        @create="createWithIndexer"
        @edit="clearIndexerError"
      />
    </template>
  </div>
</template>

<style scoped>
  /* The ring sits on the frame around the paper and not on the paper itself: the paper is a
     light surface, and the ring is drawn on the page chrome, so it needs the chrome's blue. */
  .correction-ring {
    box-shadow:
      0 0 0 2px var(--color-badge-info),
      0 0 24px 4px color-mix(in srgb, var(--color-badge-info) 42%, transparent);
  }

  .correction-pulse::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    animation: correction-pulse 0.9s ease-out 1;
  }

  @keyframes correction-pulse {
    from {
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-badge-info) 80%, transparent);
    }

    to {
      box-shadow: 0 0 0 22px color-mix(in srgb, var(--color-badge-info) 0%, transparent);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .correction-pulse::after {
      animation: none;
    }
  }
</style>
